import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, Field, Toggle, btn, btnGhost, inputClass } from "@/pages/admin/email/EmailLayout";

export default function EmailSettings() {
  const [s, setS] = useState(null);
  const [provider, setProvider] = useState(null);

  const checkProvider = async () => {
    const { data } = await api.get("/admin/email/provider/status");
    setProvider(data);
    toast[data.connected ? "success" : "error"](
      data.connected ? "Brevo connection is working" : data.error || "Brevo is not connected"
    );
  };

  useEffect(() => {
    api.get("/admin/email/settings").then((r) => setS(r.data));
    api.get("/admin/email/provider/status").then((r) => setProvider(r.data));
  }, []);

  if (!s) return null;

  const connectBrevo = async () => {
    try {
      const { data } = await api.patch("/admin/email/settings", { marketing_provider: "brevo" });
      setS(data);
      toast.success("Brevo is now the campaign provider");
      checkProvider();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 10000 });
    }
  };

  const syncSuppressions = async () => {
    try {
      const { data } = await api.post("/admin/email/provider/sync-suppressions");
      toast.success(
        `Checked ${data.checked} blocked contact(s); ${data.suppressed.length} suppressed here.`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 10000 });
    }
  };

  const save = async () => {
    const { data } = await api.patch("/admin/email/settings", {
      collection_enabled: s.collection_enabled,
      confirmation_sending_enabled: s.confirmation_sending_enabled,
      campaign_sending_enabled: s.campaign_sending_enabled,
      from_name: s.from_name,
      reply_to: s.reply_to,
      postal_address: s.postal_address,
      public_tags: s.public_tags,
    });
    setS(data);
    toast.success("Email settings saved");
  };

  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-start" data-testid="email-settings">
      <Card title="Sending switches" testid="email-settings-switches">
        <Toggle
          checked={s.collection_enabled}
          onChange={(v) => setS((x) => ({ ...x, collection_enabled: v }))}
          label="Collect new subscribers"
          description="When off, the signup forms politely decline and nothing is stored."
          testid="setting-collection"
        />
        <Toggle
          checked={s.confirmation_sending_enabled}
          onChange={(v) => setS((x) => ({ ...x, confirmation_sending_enabled: v }))}
          label="Send double opt-in confirmation emails"
          description="Required before anyone can become an active subscriber. Uses Emergent managed email."
          testid="setting-confirmation"
        />
        <Toggle
          checked={s.campaign_sending_enabled}
          onChange={(v) => setS((x) => ({ ...x, campaign_sending_enabled: v }))}
          label="Allow campaign sending"
          description="Also needs a marketing email provider connected. Bulk marketing cannot be sent through the transactional managed service."
          testid="setting-campaigns"
        />
        <div className="flex gap-3 mt-6">
          <button onClick={save} className={btn} data-testid="email-settings-save">
            Save settings
          </button>
        </div>
      </Card>

      <div className="space-y-8">
        <Card title="Sender identity" testid="email-settings-sender">
          <Field label="Sender name shown in the inbox">
            <input
              value={s.from_name}
              onChange={(e) => setS((x) => ({ ...x, from_name: e.target.value }))}
              data-testid="setting-from-name"
              className={inputClass}
            />
          </Field>
          <Field
            label="Reply-to address"
            hint="Where replies and test emails are delivered. Leave blank to use the admin account address."
          >
            <input
              value={s.reply_to || ""}
              onChange={(e) => setS((x) => ({ ...x, reply_to: e.target.value }))}
              data-testid="setting-reply-to"
              className={inputClass}
            />
          </Field>
          <Field
            label="Postal address in the email footer"
            hint="Anti-spam law requires a physical mailing address in every marketing email."
          >
            <input
              value={s.postal_address}
              onChange={(e) => setS((x) => ({ ...x, postal_address: e.target.value }))}
              data-testid="setting-postal-address"
              className={inputClass}
            />
          </Field>
          <button onClick={save} className={btn} data-testid="email-settings-save-sender">
            Save
          </button>
        </Card>

        <Card title="Marketing provider" testid="email-settings-provider">
          <div className="text-[13px] font-light text-[#5A5A5A] leading-relaxed space-y-2 mb-4">
            <p>
              Status:{" "}
              <strong className="font-normal text-charcoal" data-testid="provider-status">
                {provider?.connected
                  ? `Connected to Brevo${provider.account?.company ? ` (${provider.account.company})` : ""}`
                  : provider?.error || "checking…"}
              </strong>
            </p>
            {provider?.sender && <p>Sending address: {provider.sender}</p>}
            {provider?.usage && (
              <p data-testid="provider-quota">
                Today: {provider.usage.sent} of {provider.usage.limit} free-plan emails used,{" "}
                {provider.usage.remaining} remaining.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={checkProvider} className={btnGhost} data-testid="provider-check">
              Check connection
            </button>
            <button
              onClick={connectBrevo}
              className={btn}
              disabled={s.marketing_provider === "brevo"}
              data-testid="provider-connect"
            >
              {s.marketing_provider === "brevo" ? "Brevo selected" : "Use Brevo for campaigns"}
            </button>
            <button onClick={syncSuppressions} className={btnGhost} data-testid="provider-sync">
              Sync unsubscribes from Brevo
            </button>
          </div>
        </Card>

        <Card title="Delivery" testid="email-settings-delivery">
          <div className="text-[13px] font-light text-[#5A5A5A] leading-relaxed space-y-2">
            <p>
              <strong className="font-normal text-charcoal">Transactional:</strong>{" "}
              {s.delivery?.transactional}
            </p>
            <p>
              <strong className="font-normal text-charcoal">Marketing campaigns:</strong>{" "}
              {s.delivery?.marketing}
            </p>
            <p>
              <strong className="font-normal text-charcoal">Links in emails point to:</strong>{" "}
              {s.delivery?.site_url}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
